import { Col, Row } from 'react-bootstrap';
import React, { FunctionComponent, ReactElement, useContext, useEffect, useRef } from 'react';
import { HyruleToken } from '@nintendo/hyrule-react-commons';
import AccountDetails from './account-details/AccountDetails';
import AccountDeviceDetails from './account-device-details/AccountDeviceDetails';
import Footer from '../Footer';
import GamePlayData from './game-play/GamePlayData';
import GameRecommendations from './game-recommendations/GameRecommendations';
import HyruleTokenContext from '../contexts/HyruleTokenContext';
import NotFoundScreen from '../shared/components/nas-screens/NotFoundScreen';
import RepairsPane from '../shared/components/repairs/RepairsPane';
import ServerErrorScreen from '../shared/components/nas-screens/ServerErrorScreen';
import { SearchContext } from '../contexts/SearchContext';
import { useAccount } from './hooks/useAccount';
import { useRepairsByEmail } from './hooks/useRepairsByEmail';
import { useOrders } from './hooks/useOrders';
import Orders from './orders/Orders';

const Account: FunctionComponent = (): ReactElement => {
    const searchContext = useContext(SearchContext);
    const token: HyruleToken = useContext(HyruleTokenContext);

    const email = searchContext?.resolvedEmail || '';
    const naid = searchContext?.resolvedNaid || '';
    const orderNumber = searchContext?.resolvedOrderNumber || '';
    const correlationId = searchContext?.correlationId || '';

    const { loading: isAccountLoading, data: accountByNaid } = useAccount(
        naid,
        email,
        token,
        correlationId,
    );

    const {
        orders,
        isLoading: isOrdersLoading,
        isError: ordersHasError,
        isNotFound: ordersNotFound,
    } = useOrders(naid, email, token, correlationId);

    const {
        data: repairs,
        notFound: repairsNotFound,
        loading: isRepairsLoading,
        error: repairsHasError,
    } = useRepairsByEmail(email, token, correlationId);

    const ordersRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!isOrdersLoading && orderNumber && ordersRef.current) {
            ordersRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [orderNumber, isOrdersLoading]);

    if (searchContext?.isNintendoAccountError && !naid && ordersHasError && repairsHasError) {
        return <ServerErrorScreen />;
    }

    if (searchContext?.isNintendoAccountNotFound && !naid && ordersNotFound && repairsNotFound) {
        return <NotFoundScreen />;
    }

    return (
        <>
            <AccountDetails
                isLoading={isAccountLoading}
                account={accountByNaid.account}
                lastUpdated={accountByNaid.lastUpdated}
            />
            <AccountDeviceDetails
                isLoading={isAccountLoading}
                devices={accountByNaid.devices}
                lastUpdated={accountByNaid.lastUpdated}
            />
            <Row>
                <Col>
                    <RepairsPane
                        isLoading={isRepairsLoading}
                        repairs={repairs}
                        displayNotFound={repairsNotFound}
                        displayServerError={repairsHasError}
                        headerText="Service requests"
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <div ref={ordersRef}>
                        <Orders
                            orders={orders}
                            isError={ordersHasError}
                            isLoading={isOrdersLoading}
                            isNotFound={ordersNotFound}
                            orderNumber={orderNumber}
                        />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col lg={8}>
                    <GamePlayData />
                </Col>
                <Col lg={4}>
                    <GameRecommendations />
                </Col>
            </Row>
            <Footer />
        </>
    );
};

export default Account;
